import React from 'react'
import styled from 'styled-components'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import breakpoints from '../../styles/breakpoints'
import { ModalContent } from '../system/modal/modal'
import { transitionTimings } from '../../styles/theme'
import useModal from '../system/modal/use-modal'
import { bumpRichTextHeading } from '../../utils/rich-text-utils'
import VenueInfo from '../../models/venue-info'
import Image from '../../models/image'

const Heading = styled.h2`
  margin-top: 0;
`

const ProfileDescription = styled.div`
  @media (min-width: ${breakpoints.md}) {
    column-count: 2;
    column-gap: ${props => props.theme.spacing.base700};
  }
`

const VenueImages = styled.section`
  display: grid;
  grid-template-areas:
    'img0 img0'
    'img1 img2'
    'img3 img4';

  place-items: center;
  column-gap: ${props => props.theme.spacing.base500};
  row-gap: ${props => props.theme.spacing.base600};
  padding: ${props => props.theme.spacing.base700} 0;
  width: 100%;

  @media (min-width: ${breakpoints.sm}) {
    grid-template-areas:
      'img0 img0 img0 img1 img2'
      'img0 img0 img0 img3 img4';
  }
`

const VenueImageWrapper = styled.div`
  grid-area: ${({ index }: { index: number }) => `img${index}`};
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: transform ${transitionTimings.fast} ease;

  &:hover {
    transform: translateY(-1px);
  }
`

const VenueImage = styled.img`
  width: 100%;
  height: 100%;
  box-shadow: ${props => props.theme.shadows.close};
  border-radius: 2px;
  object-fit: cover;

  &::before {
    content: '';
    display: inline-block;
  }
`

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const ImageModalContent = styled(ModalContent)`
  background: none;
  width: 100%;
  height: 100%;
`

const VenueProfile = ({ profile }: { profile: VenueInfo }) => {
  const headingBumpedDescription = bumpRichTextHeading(2, profile.description)
  const { setModalOpen, setModalContent } = useModal()

  const selectImage = (image: Image) => {
    const img = (
      <ImageModalContent>
        <picture>
          <source srcSet={`${image.url}?fm=webp`} type="image/webp" />
          <ModalImage src={image.url} alt={image.title} />
        </picture>
      </ImageModalContent>
    )
    setModalContent(img)
    setModalOpen(true)
  }

  return (
    <>
      <Heading>{profile.heading}</Heading>
      <ProfileDescription>{documentToReactComponents(headingBumpedDescription)}</ProfileDescription>
      <VenueImages aria-label="Venue Images">
        {profile.images.map((image, index) => {
          const baseImgUrl = `${image.url}?w=${index === 0 ? 700 : 500}&fit=thumb`
          return (
            <VenueImageWrapper index={index} key={index} onClick={() => selectImage(image)}>
              <picture key={index}>
                <source srcSet={`${baseImgUrl}&fm=webp`} type="image/webp" />
                <VenueImage src={`${baseImgUrl}&q=80&fl=progressive&fm=jpg`} alt={image.title} />
              </picture>
            </VenueImageWrapper>
          )
        })}
      </VenueImages>
    </>
  )
}

export default VenueProfile
